import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faStar, faStore, faUsers, faCrown, faTimes } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import TemplateCard from './TemplateCard';
import TemplateDetailModal from './TemplateDetailModal';

const TEMPLATE_SOURCES = [
  { value: 'all', label: 'All Templates', icon: faStore },
  { value: 'personal', label: 'My Templates', icon: faUsers },
  { value: 'community', label: 'Community', icon: faUsers },
  { value: 'platform', label: 'Platform Curated', icon: faCrown },
  { value: 'vendor', label: 'Vendor Templates', icon: faStore }
];

const SORT_OPTIONS = [
  { value: 'rating', label: 'Highest Rated' },
  { value: 'usage', label: 'Most Used' },
  { value: 'newest', label: 'Newest' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' }
];

export default function TemplateMarketplace({ 
  experienceTypeId,
  onSelectTemplate,
  onClose 
}) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, [selectedSource, sortBy, experienceTypeId, user]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      let q = collection(db, 'templates');
      
      // Apply filters
      if (experienceTypeId) {
        q = query(q, where('typeId', '==', experienceTypeId));
      }
      
      if (selectedSource === 'personal' && user?.uid) {
        q = query(q, where('createdBy', '==', user.uid));
      } else if (selectedSource === 'community') {
        q = query(q, where('visibility', '==', 'community'));
      } else if (selectedSource === 'platform') {
        q = query(q, where('createdBy', '==', 'platform'));
      }
      // Note: vendor templates are filtered client-side (Firestore doesn't support != null)

      // Apply sorting
      if (sortBy === 'rating') {
        q = query(q, orderBy('rating', 'desc'));
      } else if (sortBy === 'usage') {
        q = query(q, orderBy('usageCount', 'desc'));
      } else if (sortBy === 'newest') {
        q = query(q, orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);
      let templatesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter vendor templates client-side (Firestore doesn't support != null)
      if (selectedSource === 'vendor') {
        templatesData = templatesData.filter(template => template.vendorId != null);
      }

      // Filter by search query
      if (searchQuery) {
        const queryLower = searchQuery.toLowerCase();
        templatesData = templatesData.filter(template =>
          template.name?.toLowerCase().includes(queryLower) ||
          template.description?.toLowerCase().includes(queryLower)
        );
      }

      // Sort by price if needed
      if (sortBy === 'price-low') {
        templatesData.sort((a, b) => (a.price || 0) - (b.price || 0));
      } else if (sortBy === 'price-high') {
        templatesData.sort((a, b) => (b.price || 0) - (a.price || 0));
      }

      setTemplates(templatesData);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    if (onSelectTemplate) {
      onSelectTemplate(template);
    }
    setSelectedTemplate(null);
    if (onClose) onClose();
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-text-primary mb-1">Template Marketplace</h2>
            <p className="text-sm text-text-secondary">
              Browse templates to jumpstart your planning
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-text-secondary" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-border bg-gray-50">
          {/* Search Bar */}
          <div className="relative mb-4">
            <FontAwesomeIcon 
              icon={faSearch} 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-12 pr-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Source Filters */}
          <div className="flex items-center gap-4 mb-4">
            <span className="text-sm font-semibold text-text-primary">Source:</span>
            <div className="flex-1 flex gap-2 overflow-x-auto">
              {TEMPLATE_SOURCES.map(source => (
                <button
                  key={source.value}
                  onClick={() => setSelectedSource(source.value)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors flex items-center gap-2 ${
                    selectedSource === source.value
                      ? 'bg-primary text-white'
                      : 'bg-white border border-border text-text-secondary hover:border-primary'
                  }`}
                >
                  <FontAwesomeIcon icon={source.icon} />
                  {source.label}
                </button>
              ))}
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg bg-white focus:ring-2 focus:ring-primary focus:border-primary"
            >
              {SORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Template List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-text-secondary">Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faStore} className="text-4xl text-text-secondary mb-4" />
              <p className="text-text-secondary mb-2">No templates found</p>
              <p className="text-sm text-text-secondary">
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onClick={() => setSelectedTemplate(template)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Template Detail Modal */}
      {selectedTemplate && (
        <TemplateDetailModal
          template={selectedTemplate}
          onSelect={handleSelectTemplate}
          onClose={() => setSelectedTemplate(null)}
        />
      )}
    </div>,
    document.body
  );
}
